import React from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react-lite';
import { Chart, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  ScatterController,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Skeleton } from 'primereact/skeleton';
import GeneratingLoader from 'components/generatingLoader/generatingLoader';
import { productAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './PricingTab.styles.scss';
import { PricingTabProps } from './PriceTab.type';
import usePriceTab from './usePriceTab.logic';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  ScatterController,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  zoomPlugin
);

const PricingTab: React.FC<PricingTabProps> = observer(() => {
  const {
    priceView,
    setPriceView,
    isChartModalOpen,
    setIsChartModalOpen,
    priceBoxPlotRef,
    scatterPlotData,
    scatterPlotOptions,
    scatterPlotModalOptions,
    labelerKeys,
    getLabelerColor,
    formatLabelerName,
    // Trend granularity (monthly/quarterly) - no refetch needed!
    trendGranularity,
    setTrendGranularity,
    // Current trend data based on selected granularity
    currentTrendData,
    // Current Price Band (separate loader)
    currentBandData,
    isCurrentBandLoading,
    currentBandError,
    // Historical Trend (separate loader)
    isHistoricalTrendLoading,
    historicalTrendError,
    // Labeler Pricing (separate loader)
    isLabelerPricingLoading,
    labelerPricingError,
    // Current Price per Labeler bar chart
    currentPriceBarData,
    currentPriceBarOptions,
    // AI Insights
    aiInsights,
    isInsightsLoading,
    insightsError,
    // Price Elasticity data (separate loader)
    isPriceElasticityLoading,
    priceElasticityError,
    // Price Elasticity scatter plot
    priceElasticityScatterData,
    priceElasticityScatterOptions,
    // Zoom state and controls
    isPriceElasticityZoomed,
    priceElasticityChartRef,
    resetPriceElasticityZoom,
    // Historical Trend zoom state and controls
    isHistoricalTrendZoomed,
    historicalTrendChartRef,
    resetHistoricalTrendZoom,
    // Historical Trend modal zoom state and controls
    isHistoricalTrendModalZoomed,
    historicalTrendModalChartRef,
    resetHistoricalTrendModalZoom,
  } = usePriceTab(productAnalysisStore);

  return (
    <>
      <section className="content-section">
        <div className="section-header">
          <h3 className="section-title">Pricing Intelligence</h3>
        </div>
        <div className="section-content">
          <div className="pricing-grid">
            {/* Current Market Price Band Section */}
            <div>
              <h4 className="price-band-box-text-title">
                Current Market Price Band (Last 90 Days)
              </h4>
              <div className="price-band-chart">
                {isCurrentBandLoading ? (
                  <div style={{ padding: '1rem', width: '100%', height: '100%' }}>
                    <Skeleton width="100%" height="200px" borderRadius="0.5rem" />
                    <div style={{ marginTop: '1rem' }}>
                      <Skeleton width="60%" height="1rem" style={{ marginBottom: '0.5rem' }} />
                      <Skeleton width="80%" height="0.8rem" style={{ marginBottom: '0.3rem' }} />
                      <Skeleton width="70%" height="0.8rem" style={{ marginBottom: '0.3rem' }} />
                      <Skeleton width="75%" height="0.8rem" />
                    </div>
                  </div>
                ) : currentBandError ? (
                  <div
                    className="error-message"
                    style={{ padding: '2rem', textAlign: 'center', color: '#e74c3c' }}
                  >
                    {currentBandError}
                  </div>
                ) : currentBandData &&
                  currentBandData.median !== 0 &&
                  currentBandData.q1 !== 0 &&
                  currentBandData.q3 !== 0 ? (
                  <>
                    <canvas ref={priceBoxPlotRef} width={600} height={280}></canvas>
                    <div className="boxplot-legend">
                      <div className="boxplot-legend-title">Box Plot Statistics</div>
                      <div className="boxplot-legend-items">
                        <div className="boxplot-legend-item">
                          <span className="legend-marker min"></span>
                          <span className="legend-label">Min:</span>
                          <span className="legend-value">${currentBandData.min.toFixed(2)}</span>
                        </div>
                        <div className="boxplot-legend-item">
                          <span className="legend-marker q1"></span>
                          <span className="legend-label">Q1 (25%):</span>
                          <span className="legend-value">${currentBandData.q1.toFixed(2)}</span>
                        </div>
                        <div className="boxplot-legend-item">
                          <span className="legend-marker median"></span>
                          <span className="legend-label">Median:</span>
                          <span className="legend-value">${currentBandData.median.toFixed(2)}</span>
                        </div>
                        <div className="boxplot-legend-item">
                          <span className="legend-marker q3"></span>
                          <span className="legend-label">Q3 (75%):</span>
                          <span className="legend-value">${currentBandData.q3.toFixed(2)}</span>
                        </div>
                        <div className="boxplot-legend-item">
                          <span className="legend-marker max"></span>
                          <span className="legend-label">Max:</span>
                          <span className="legend-value">${currentBandData.max.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="boxplot-summary">
                        <span className="summary-item">
                          <strong>IQR:</strong> $
                          {(currentBandData.q3 - currentBandData.q1).toFixed(2)}
                        </span>
                        <span className="summary-divider">|</span>
                        <span className="summary-item">
                          <strong>Spread:</strong> {currentBandData.spread.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="no-data-message" style={{ padding: '2rem', textAlign: 'center' }}>
                    No data available
                  </div>
                )}
              </div>
            </div>

            {/* Historical Pricing Trend Section */}
            <div>
              <div className="price-trend-header">
                <h4 className="price-trend-header-title">
                  Historical Pricing Trend (
                  {trendGranularity === 'monthly' ? '12 Months' : '4 Quarters'})
                </h4>
                <div className="price-trend-header-actions">
                  <div className="granularity-toggle">
                    <button
                      className={`toggle-btn ${trendGranularity === 'monthly' ? 'active' : ''}`}
                      onClick={() => setTrendGranularity('monthly')}
                    >
                      Monthly
                    </button>
                    <button
                      className={`toggle-btn ${trendGranularity === 'quarterly' ? 'active' : ''}`}
                      onClick={() => setTrendGranularity('quarterly')}
                    >
                      Quarterly
                    </button>
                  </div>
                  <div className="price-view-toggle">
                    <button
                      className={`toggle-btn ${priceView === 'chart' ? 'active' : ''}`}
                      onClick={() => setPriceView('chart')}
                    >
                      Chart
                    </button>
                    <button
                      className={`toggle-btn ${priceView === 'table' ? 'active' : ''}`}
                      onClick={() => setPriceView('table')}
                    >
                      Table
                    </button>
                  </div>
                </div>
              </div>

              {isHistoricalTrendLoading ? (
                <div className="price-trend-chart" style={{ padding: '1rem' }}>
                  <Skeleton width="100%" height="350px" borderRadius="0.5rem" />
                </div>
              ) : historicalTrendError ? (
                <div className="price-trend-chart">
                  <div
                    className="error-message"
                    style={{ padding: '2rem', textAlign: 'center', color: '#e74c3c' }}
                  >
                    {historicalTrendError}
                  </div>
                </div>
              ) : priceView === 'chart' ? (
                <div className="price-trend-chart">
                  {scatterPlotData && (
                    <button
                      className="chart-magnifier-btn-inline"
                      onClick={() => setIsChartModalOpen(true)}
                      aria-label="View chart in full screen"
                      title="View chart in full screen"
                    >
                      <i className="pi pi-search-plus"></i>
                    </button>
                  )}
                  {scatterPlotData ? (
                    <>
                      <Chart
                        ref={historicalTrendChartRef}
                        type="scatter"
                        data={scatterPlotData}
                        options={scatterPlotOptions}
                      />
                      {isHistoricalTrendZoomed && (
                        <button
                          className="chart-reset-zoom-btn"
                          onClick={resetHistoricalTrendZoom}
                          aria-label="Reset zoom"
                          title="Reset zoom"
                        >
                          <i className="pi pi-refresh"></i>
                          Reset Zoom
                        </button>
                      )}
                    </>
                  ) : (
                    <div
                      className="no-data-message"
                      style={{ textAlign: 'center', padding: '2rem' }}
                    >
                      No data available
                    </div>
                  )}
                </div>
              ) : (
                <div className="price-trend-table">
                  {currentTrendData && currentTrendData.length > 0 && labelerKeys.length > 0 && (
                    <button
                      className="chart-magnifier-btn-inline"
                      onClick={() => setIsChartModalOpen(true)}
                      aria-label="View table in full screen"
                      title="View table in full screen"
                    >
                      <i className="pi pi-search-plus"></i>
                    </button>
                  )}
                  {currentTrendData && currentTrendData.length > 0 && labelerKeys.length > 0 ? (
                    <div className="table-wrapper">
                      <table className="pricing-table">
                        <thead>
                          <tr>
                            <th>Period</th>
                            {labelerKeys.map((labelerKey, index) => (
                              <th key={labelerKey} style={{ color: getLabelerColor(index) }}>
                                {formatLabelerName(labelerKey)}
                              </th>
                            ))}
                            <th style={{ fontWeight: 'bold' }}>Market Avg</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentTrendData.map((row, index) => (
                            <tr key={index}>
                              <td>{row.period}</td>
                              {labelerKeys.map((labelerKey, labelerIndex) => {
                                const rowRecord = row as unknown as Record<string, number>;
                                const value = rowRecord[labelerKey] || 0;
                                return (
                                  <td
                                    key={labelerKey}
                                    style={{ color: getLabelerColor(labelerIndex) }}
                                  >
                                    ${value.toFixed(2)}
                                  </td>
                                );
                              })}
                              <td style={{ fontWeight: 'bold' }}>${row.average.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div
                      className="no-data-message"
                      style={{ padding: '2rem', textAlign: 'center' }}
                    >
                      No data available
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Price Elasticity & Current Price per Labeler Section - Using same grid as above */}
          <div className="pricing-grid" style={{ marginTop: '1.5rem' }}>
            {/* Price Elasticity Chart */}
            <div>
              <h4 className="price-band-box-text-title" style={{ marginTop: '1.5rem' }}>
                Price Elasticity
              </h4>
              <div className="price-band-chart">
                {isPriceElasticityLoading ? (
                  <div style={{ padding: '1rem', width: '100%', height: '100%' }}>
                    <Skeleton width="100%" height="250px" borderRadius="0.5rem" />
                  </div>
                ) : priceElasticityError ? (
                  <div
                    className="error-message"
                    style={{ padding: '2rem', textAlign: 'center', color: '#e74c3c' }}
                  >
                    {priceElasticityError}
                  </div>
                ) : priceElasticityScatterData ? (
                  <>
                    <Chart
                      ref={priceElasticityChartRef}
                      type="scatter"
                      data={priceElasticityScatterData}
                      options={priceElasticityScatterOptions}
                    />
                    {isPriceElasticityZoomed && (
                      <button
                        className="chart-reset-zoom-btn"
                        onClick={resetPriceElasticityZoom}
                        aria-label="Reset zoom"
                        title="Reset zoom"
                      >
                        <i className="pi pi-refresh"></i>
                        Reset Zoom
                      </button>
                    )}
                  </>
                ) : (
                  <div className="no-data-message" style={{ padding: '2rem', textAlign: 'center' }}>
                    No price elasticity data available
                  </div>
                )}
              </div>
            </div>

            {/* Current Price per Labeler Bar Chart (VA/DOD sources) */}
            <div>
              <h4 className="price-band-box-text-title" style={{ marginTop: '1.5rem' }}>
                Current Price per Labeler
              </h4>
              <div className="price-band-chart">
                {isLabelerPricingLoading ? (
                  <div style={{ padding: '1rem', width: '100%', height: '100%' }}>
                    <Skeleton width="100%" height="250px" borderRadius="0.5rem" />
                  </div>
                ) : labelerPricingError ? (
                  <div
                    className="error-message"
                    style={{ padding: '2rem', textAlign: 'center', color: '#e74c3c' }}
                  >
                    {labelerPricingError}
                  </div>
                ) : currentPriceBarData ? (
                  <Bar data={currentPriceBarData} options={currentPriceBarOptions} />
                ) : (
                  <div className="no-data-message" style={{ padding: '2rem', textAlign: 'center' }}>
                    No VA/DOD pricing data available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* AI-Generated Pricing Strategy Recommendations Section */}
          {isInsightsLoading ? (
            <div className="narrative-box">
              <h4 className="narrative-box-text-title">Pricing Strategy Recommendations</h4>
              <div className="narrative-content">
                <GeneratingLoader />
              </div>
            </div>
          ) : insightsError ? (
            <div className="narrative-box error">
              <h4 className="narrative-box-text-title">Pricing Strategy Recommendations</h4>
              <div className="narrative-content">
                <div className="insights-error-container">
                  <i className="fas fa-exclamation-triangle insights-error-icon" />
                  <div className="insights-error-details">
                    <p className="insights-error-message">{insightsError}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : aiInsights && aiInsights.length > 0 ? (
            <div className="narrative-box">
              <h4 className="narrative-box-text-title">Pricing Strategy Recommendations</h4>
              <ul className="ai-insights-list">
                {aiInsights.map((insight, index) => (
                  <li key={index} className="insight-item">
                    <span className="narrative-box-text-item">{insight.bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="narrative-box">
              <h4 className="narrative-box-text-title">Pricing Strategy Recommendations</h4>
              <div className="no-data-message" style={{ padding: '1rem' }}>
                No recommendations available
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Chart/Table Modal - Rendered via Portal at document body level */}
      {isChartModalOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="chart-modal-overlay" onClick={() => setIsChartModalOpen(false)}>
            <div className="chart-modal-content" onClick={e => e.stopPropagation()}>
              <div className="chart-modal-header">
                <h3>
                  Historical Pricing Trend (
                  {trendGranularity === 'monthly' ? '12 Months' : '4 Quarters'})
                </h3>
                <button
                  className="chart-modal-close-btn"
                  onClick={() => setIsChartModalOpen(false)}
                  aria-label="Close modal"
                >
                  <i className="pi pi-times"></i>
                </button>
              </div>
              <div className="chart-modal-body">
                {priceView === 'chart' && scatterPlotData ? (
                  <>
                    <Chart
                      ref={historicalTrendModalChartRef}
                      type="scatter"
                      data={scatterPlotData}
                      options={scatterPlotModalOptions}
                    />
                    {isHistoricalTrendModalZoomed && (
                      <button
                        className="chart-reset-zoom-btn"
                        onClick={resetHistoricalTrendModalZoom}
                        aria-label="Reset zoom"
                        title="Reset zoom"
                      >
                        <i className="pi pi-refresh"></i>
                        Reset Zoom
                      </button>
                    )}
                  </>
                ) : (
                  <div className="modal-table-container">
                    <table className="pricing-table modal-table">
                      <thead>
                        <tr>
                          <th>Period</th>
                          {labelerKeys.map((labelerKey, index) => (
                            <th key={labelerKey} style={{ color: getLabelerColor(index) }}>
                              {formatLabelerName(labelerKey)}
                            </th>
                          ))}
                          <th style={{ fontWeight: 'bold' }}>Market Avg</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentTrendData.map((row, index) => (
                          <tr key={index}>
                            <td>{row.period}</td>
                            {labelerKeys.map((labelerKey, labelerIndex) => {
                              const rowRecord = row as unknown as Record<string, number>;
                              const value = rowRecord[labelerKey] || 0;
                              return (
                                <td
                                  key={labelerKey}
                                  style={{ color: getLabelerColor(labelerIndex) }}
                                >
                                  ${value.toFixed(2)}
                                </td>
                              );
                            })}
                            <td style={{ fontWeight: 'bold' }}>${row.average.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
});

export default PricingTab;
